import { useState } from 'react';
import { useAddSingleMealToMealPlan } from './addSingleMealToMealPlan';
import supabase from '../supabaseClient';
import { addCsrfHeader } from '../utils/csrfUtils';

export const useUpdateMealPlanPreferences = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const { addSingleMealToMealPlan } = useAddSingleMealToMealPlan();

  const updateMealPlanPreferences = async (mealPlanId, appUserId, newBreakfastMeals, breakfastServingSize, newLunchMeals, lunchServingSize, newDinnerMeals, dinnerServingSize, previousBreakfastMeals, previousLunchMeals, previousDinnerMeals) => {
    setIsUpdating(true);
    setError(null);

    try {
      // Get the current session for the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      // Add CSRF token and auth header
      const headers = await addCsrfHeader({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      });

      //update the meal plan preferences in the database
      const response = await fetch('/api/v1/meal-plan/update_meal_plan_preferences', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          meal_plan_id: mealPlanId,
          app_user_id: appUserId,
          preferred_number_of_breakfast_meals: newBreakfastMeals,
          preferred_number_of_lunch_meals: newLunchMeals,
          preferred_number_of_dinner_meals: newDinnerMeals
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update meal plan preferences');
      }

      const data = await response.json();
      console.log('Meal plan preferences updated successfully:', data);

      //get current number of meals for each meal time in the meal plan, then set previous values to these
      const mealPlanMeals = await fetch('/api/v1/meal-plan/' + mealPlanId + '/app_user/' + appUserId, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const mealPlanMealsData = await mealPlanMeals.json();

      const currentBreakfastMeals = mealPlanMealsData.meals.filter(meal => meal.meal_of_day === 'breakfast').length;
      const currentLunchMeals = mealPlanMealsData.meals.filter(meal => meal.meal_of_day === 'lunch').length;
      const currentDinnerMeals = mealPlanMealsData.meals.filter(meal => meal.meal_of_day === 'dinner').length;

      previousBreakfastMeals = currentBreakfastMeals;
      previousLunchMeals = currentLunchMeals;
      previousDinnerMeals = currentDinnerMeals;

      // Add meals if counts increased
      if (newBreakfastMeals > previousBreakfastMeals) {
        for (let i = 0; i < newBreakfastMeals - previousBreakfastMeals; i++) {
          await addSingleMealToMealPlan(mealPlanId, appUserId, 'breakfast', breakfastServingSize, null);
        }
      }

      if (newLunchMeals > previousLunchMeals) {
        for (let i = 0; i < newLunchMeals - previousLunchMeals; i++) {
          await addSingleMealToMealPlan(mealPlanId, appUserId, 'lunch', lunchServingSize, null);
        }
      }

      if (newDinnerMeals > previousDinnerMeals) {
        for (let i = 0; i < newDinnerMeals - previousDinnerMeals; i++) {
          await addSingleMealToMealPlan(mealPlanId, appUserId, 'dinner', dinnerServingSize, null);
        }
      }

      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error updating meal plan preferences:', err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateMealPlanPreferences,
    isUpdating,
    error
  };
};

export default useUpdateMealPlanPreferences;

